import { useEffect, useState, useMemo } from "react";
import ReactSelect from "react-select";
import { 
  createReceta, 
  updateReceta,
  listRecetasLazy, 
  deleteReceta, 
  listcocinaInsumoLazy 
} from "../api/ventaCocinaInsumo";
import { listProductosLazy } from "../api/productos";
import {
  FiPlus, FiSearch, FiEdit, FiTrash2, FiInfo,
  FiFileText, FiLayers, FiMinus, FiEye, FiImage,
  FiDollarSign, FiTrendingUp, FiPieChart
} from "react-icons/fi";
import Loader from "../components/Loader";
import { showConfirmAlert } from "../utils/alertHelper";

const Recetas = () => {
  const [loading, setLoading] = useState(true);
  const [recetasAgrupadas, setRecetasAgrupadas] = useState([]);
  const [productos, setProductos] = useState([]);
  const [insumos, setInsumos] = useState([]);
  
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedReceta, setSelectedReceta] = useState(null);
  
  const [editMode, setEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [buscar, setBuscar] = useState("");

  const [form, setForm] = useState({
    id_producto: "",
    nombre: "",
    status: 1,
    detalle: [],
  });

  const fetchData = async (p = 1) => {
    setLoading(true);
    try {
      const resp = await listRecetasLazy(p, limit, buscar);
      if (resp && resp.data) {
        const grouped = resp.data.reduce((acc, current) => {
          const recipeHeader = current.CocinaRecetum;
          if (!recipeHeader) return acc;
          
          if (!acc[recipeHeader.id]) {
            acc[recipeHeader.id] = {
              ...recipeHeader,
              detalles: []
            };
          }
          
          acc[recipeHeader.id].detalles.push({
            id: current.id,
            id_cocina_insumo: current.id_cocina_insumo,
            cantidad: current.cantidad,
            Insumo: current.CocinaInsumo
          });
          
          return acc;
        }, {});

        setRecetasAgrupadas(Object.values(grouped));
        setTotalPages(resp.totalPages || 1);
        setPage(resp.currentPage || 1);
      }
    } catch (error) {
      console.error("Error al obtener recetas", error);
    } finally {
      setLoading(false);
    }
  };

  const loadDependencies = async () => {
    try {
      const resProds = await listProductosLazy(1, 1000, "");
      const resInsumos = await listcocinaInsumoLazy(1, 1000, "");
      setProductos(resProds.data || []);
      setInsumos(resInsumos.data || []);
    } catch (error) {
      console.error("Error cargando dependencias", error);
    }
  };

  useEffect(() => {
    fetchData(page);
    loadDependencies();
    // eslint-disable-next-line
  }, [page, limit, buscar]);

  // Cálculos de costos financieros
  const calculateFinancials = (detallesForm, idProd) => {
    const totalCosto = detallesForm.reduce((acc, d) => {
      const insumo = insumos.find(i => i.id.toString() === d.id_cocina_insumo.toString());
      const precioUnit = parseFloat(insumo?.price || 0);
      const cant = parseFloat(d.cantidad || 0);
      return acc + (precioUnit * cant);
    }, 0);

    const producto = productos.find(p => p.id.toString() === idProd.toString());
    const precioVenta = parseFloat(producto?.price || 0);
    const ganancia = precioVenta - totalCosto;
    const margen = precioVenta > 0 ? (ganancia / precioVenta) * 100 : 0;

    return { totalCosto, precioVenta, ganancia, margen };
  };

  // Memoized financials for editing form
  const currentFinancials = useMemo(() => {
    return calculateFinancials(form.detalle, form.id_producto);
  }, [form.detalle, form.id_producto, insumos, productos]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (val, name) => {
    setForm(prev => ({ ...prev, [name]: val ? val.value : "" }));
  };

  const handleAddDetalle = () => {
    setForm(prev => ({
      ...prev,
      detalle: [...prev.detalle, { id_cocina_insumo: "", cantidad: "" }]
    }));
  };

  const handleRemoveDetalle = (index) => {
    const newDetalle = [...form.detalle];
    newDetalle.splice(index, 1);
    setForm(prev => ({ ...prev, detalle: newDetalle }));
  };

  const handleDetalleChange = (index, field, value) => {
    const newDetalle = [...form.detalle];
    newDetalle[index][field] = value;
    setForm(prev => ({ ...prev, detalle: newDetalle }));
  };

  const openEditModal = (receta) => {
    setSelectedReceta(receta);
    setEditMode(true);
    setForm({
      id_producto: receta.id_producto?.toString() || "",
      nombre: receta.nombre || "",
      status: receta.status !== undefined ? receta.status : 1,
      detalle: receta.detalles.map(d => ({
        id_cocina_insumo: d.id_cocina_insumo?.toString() || "",
        cantidad: d.cantidad || ""
      }))
    });
    setShowModal(true);
  };

  const openViewModal = (receta) => {
    setSelectedReceta(receta);
    setShowViewModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.id_producto || !form.nombre || form.detalle.length === 0) {
      return alert("Por favor complete todos los campos y añada al menos un insumo.");
    }
    
    setSubmitting(true);
    try {
      const payload = {
        id_producto: parseInt(form.id_producto),
        nombre: form.nombre,
        status: parseInt(form.status),
        detalle: form.detalle.map(d => ({
          id_cocina_insumo: parseInt(d.id_cocina_insumo),
          cantidad: parseFloat(d.cantidad)
        }))
      };

      if (editMode && selectedReceta) {
        await updateReceta(selectedReceta.id, payload);
      } else {
        await createReceta(payload);
      }
      
      setShowModal(false);
      fetchData(page);
    } catch (error) {
      console.error("Error al guardar receta", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const confirm = await showConfirmAlert("¿Eliminar Receta?", "¿Esta acción no se puede deshacer.");
    if (confirm) {
      await deleteReceta(id);
      fetchData(page);
    }
  };

  const opcionesProductos = productos.map(p => ({
    value: p.id.toString(),
    label: p.name
  }));

  const opcionesInsumos = insumos.map(i => ({
    value: i.id.toString(),
    label: `${i.name} (${i.UnidadMedida?.name || 'S/N'})`
  }));

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight">Análisis de Recetas</h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Costos de insumos y proyecciones de rentabilidad</p>
        </div>
        <button
          onClick={() => {
            setEditMode(false);
            setForm({ id_producto: "", nombre: "", status: 1, detalle: [] });
            setShowModal(true);
          }}
          className="bg-indigo-600 text-white px-4 py-2.5 rounded-xl hover:bg-indigo-700 transition-all shadow-sm font-medium flex items-center justify-center gap-2"
        >
          <FiPlus size={20} />
          Crear Receta
        </button>
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-4 mb-6 bg-white p-4 rounded-xl shadow-sm border border-slate-200">
        <div className="relative flex-grow">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar recetas por nombre o producto..."
            value={buscar}
            onChange={(e) => {
              setBuscar(e.target.value);
              setPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 font-medium whitespace-nowrap">Mostrar:</span>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            className="border border-slate-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-sm"
          >
            {[10, 15, 25, 50, 100, 250, 500].map(num => (
              <option key={num} value={num}>
                {num} registros
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden relative">
        {loading && (
          <div className="absolute inset-0 bg-white/70 flex justify-center items-center z-10">
            <Loader />
          </div>
        )}
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500 font-semibold uppercase text-[10px] tracking-wider border-b border-slate-200">
              <tr>
                <th className="px-6 py-4">Producto</th>
                <th className="px-6 py-4">Receta</th>
                <th className="px-6 py-4">Detalle Insumos</th>
                <th className="px-6 py-4 text-right">P. Venta</th>
                <th className="px-6 py-4 text-right">P. Costo</th>
                <th className="px-6 py-4 text-right">Utilidad</th>
                <th className="px-6 py-4 text-center">Estado</th>
                <th className="px-6 py-4 text-center sticky right-0 bg-slate-50 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)]">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {recetasAgrupadas.length > 0 ? (
                recetasAgrupadas.map(r => {
                   const { totalCosto, precioVenta, ganancia } = calculateFinancials(r.detalles.map(d => ({ id_cocina_insumo: d.id_cocina_insumo, cantidad: d.cantidad })), r.id_producto);
                   return (
                    <tr key={r.id} className="hover:bg-indigo-50/30 transition-colors group">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg border border-slate-200 overflow-hidden bg-white shrink-0">
                            {r.Producto?.photo ? (
                              <img 
                                src={`${process.env.REACT_APP_RUTA_PRODUCTOS}${r.Producto.photo}`} 
                                alt={r.Producto.name} 
                                className="w-full h-full object-contain"
                              />
                            ) : (
                              <FiImage className="w-full h-full p-2 text-slate-300" />
                            )}
                          </div>
                          <div>
                            <p className="font-bold text-slate-700 leading-tight">{r.Producto?.name}</p>
                            <p className="text-[10px] text-slate-400 font-mono">SKU: {r.Producto?.sku}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-600 font-medium">{r.nombre}</td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1 max-w-[220px]">
                          {r.detalles?.map((d, idx) => (
                            <span key={idx} className="bg-slate-50 text-slate-600 text-[9px] font-bold px-1.5 py-0.5 rounded border border-slate-200 flex items-center gap-1 shadow-sm">
                              {d.Insumo?.name}: <span className="text-indigo-600">{d.cantidad}</span> <span className="text-[8px] text-slate-400 font-normal">{d.Insumo?.UnidadMedida?.name}</span>
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-slate-700">
                        ${precioVenta.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-slate-600">
                        ${totalCosto.toFixed(2)}
                      </td>
                      <td className={`px-6 py-4 text-right font-black ${ganancia > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        ${ganancia.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${r.status === 1 ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                          {r.status === 1 ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center sticky right-0 bg-white group-hover:bg-slate-50 transition-colors shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.03)] border-l border-slate-100">
                        <div className="flex justify-center gap-1">
                          <button 
                            onClick={() => openViewModal(r)}
                            className="p-2 text-indigo-500 hover:bg-indigo-100 rounded-lg transition-colors"
                            title="Ver Rentabilidad"
                          >
                            <FiEye size={16} />
                          </button>
                          <button 
                            onClick={() => openEditModal(r)}
                            className="p-2 text-blue-500 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <FiEdit size={16} />
                          </button>
                          <button 
                            onClick={() => handleDelete(r.id)}
                            className="p-2 text-rose-500 hover:bg-rose-100 rounded-lg transition-colors"
                            title="Eliminar"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                   );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-10 text-slate-400 italic">No se encontraron recetas configuradas.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginación — Estilo Identico a Productos.jsx */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-4 sm:mt-6 px-1 gap-3">
        <p className="text-sm text-slate-500 text-center sm:text-left">
          Página <span className="font-medium">{page}</span> de <span className="font-medium">{totalPages}</span>
        </p>
        <div className="flex gap-1 bg-white p-1 rounded-lg border border-slate-200 justify-center overflow-x-auto">
          <button
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${page === 1
              ? "text-slate-400 cursor-not-allowed"
              : "text-slate-600 hover:bg-slate-100"
              }`}
          >
            ← Ant
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => {
            if (totalPages > 7) {
              if (num === 1 || num === totalPages || (num >= page - 1 && num <= page + 1)) {
                return (
                  <button
                    key={num}
                    onClick={() => setPage(num)}
                    className={`min-w-[32px] px-2 py-1.5 rounded-md text-sm font-medium transition-colors ${page === num
                      ? "bg-indigo-50 text-indigo-600"
                      : "text-slate-600 hover:bg-slate-100"
                      }`}
                  >
                    {num}
                  </button>
                );
              } else if (num === page - 2 || num === page + 2) {
                return <span key={num} className="px-2 py-1.5 text-slate-400">...</span>;
              }
              return null;
            }

            return (
              <button
                key={num}
                onClick={() => setPage(num)}
                className={`min-w-[32px] px-2 py-1.5 rounded-md text-sm font-medium transition-colors ${page === num
                  ? "bg-indigo-50 text-indigo-600"
                  : "text-slate-600 hover:bg-slate-100"
                  }`}
              >
                {num}
              </button>
            )
          })}

          <button
            disabled={page === totalPages}
            onClick={() => setPage(p => p + 1)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${page === totalPages
              ? "text-slate-400 cursor-not-allowed"
              : "text-slate-600 hover:bg-slate-100"
              }`}
          >
            Sig →
          </button>
        </div>
      </div>

      {/* Modal - Rentabilidad (Ver) */}
      {showViewModal && selectedReceta && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-xl rounded-2xl shadow-2xl flex flex-col animate-zoom-in">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <FiPieChart className="text-indigo-600" size={20} />
                <h3 className="font-bold text-slate-800 text-lg">Análisis de Rentabilidad</h3>
              </div>
              <button onClick={() => setShowViewModal(false)} className="p-2 hover:bg-slate-200 rounded-lg transition-colors">
                <FiMinus size={20} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[70vh] custom-scrollbar">
              <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-100 shadow-sm mb-6">
                 <div className="w-16 h-16 rounded-xl bg-slate-50 border border-slate-200 overflow-hidden shrink-0">
                   {selectedReceta.Producto?.photo ? (
                      <img 
                        src={`${process.env.REACT_APP_RUTA_PRODUCTOS}${selectedReceta.Producto.photo}`} 
                        alt={selectedReceta.Producto.name} 
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <FiImage className="w-full h-full p-2 text-slate-300" />
                    )}
                 </div>
                 <div>
                    <h4 className="font-bold text-slate-800 text-lg leading-tight">{selectedReceta.Producto?.name}</h4>
                    <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-bold rounded uppercase">
                       {selectedReceta.nombre}
                    </span>
                 </div>
              </div>

              {/* Grid resumen */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                {(() => {
                  const fin = calculateFinancials(selectedReceta.detalles.map(d => ({ id_cocina_insumo: d.id_cocina_insumo, cantidad: d.cantidad })), selectedReceta.id_producto);
                  return (
                    <>
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                         <p className="text-[10px] font-bold text-slate-400 uppercase">Costo Producción</p>
                         <p className="text-xl font-black text-slate-700">${fin.totalCosto.toFixed(2)}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                         <p className="text-[10px] font-bold text-slate-400 uppercase">Precio Venta</p>
                         <p className="text-xl font-black text-slate-700">${fin.precioVenta.toFixed(2)}</p>
                      </div>
                      <div className={`p-4 rounded-xl border col-span-2 flex justify-between items-center ${fin.ganancia > 0 ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                         <div>
                            <p className={`text-[10px] font-bold uppercase ${fin.ganancia > 0 ? 'text-emerald-500' : 'text-rose-500'}`}>Utilidad Proyectada</p>
                            <p className={`text-2xl font-black ${fin.ganancia > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>${fin.ganancia.toFixed(2)}</p>
                         </div>
                         <div className={`px-4 py-2 rounded-lg font-black text-lg ${fin.ganancia > 0 ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'}`}>
                            {fin.margen.toFixed(1)}%
                         </div>
                      </div>
                    </>
                  );
                })()}
              </div>

              <div className="space-y-2">
                <p className="text-[10px] font-bold text-slate-400 uppercase px-2">Desglose de Insumos</p>
                {selectedReceta.detalles?.map((d, idx) => (
                  <div key={idx} className="flex justify-between items-center p-3 bg-white border border-slate-100 rounded-xl">
                    <div className="flex-1">
                      <p className="text-sm font-bold text-slate-700">{d.Insumo?.name}</p>
                      <p className="text-[10px] text-slate-400 italic">
                         {d.cantidad} {d.Insumo?.UnidadMedida?.name} × ${parseFloat(d.Insumo?.price || 0).toFixed(2)}
                      </p>
                    </div>
                    <p className="font-bold text-slate-600 text-sm">
                       ${(parseFloat(d.Insumo?.price || 0) * d.cantidad).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-5 bg-slate-50 border-t border-slate-100 rounded-b-2xl flex justify-end">
               <button onClick={() => setShowViewModal(false)} className="px-8 py-2.5 bg-indigo-600 shadow-sm text-white rounded-xl font-bold hover:bg-indigo-700 transition-all text-sm">
                 Entendido
               </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal - Creación / Edición con resumen en vivo */}
      {showModal && (
        <div className="fixed inset-0 z-[105] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl flex flex-col animate-fade-in-up my-8">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center">
                  {editMode ? <FiEdit size={20} /> : <FiLayers size={20} />}
                </div>
                <div>
                   <h3 className="text-xl font-bold text-slate-800">
                      {editMode ? "Actualizar Receta" : "Configurar Nueva Receta"}
                   </h3>
                   <p className="text-xs text-slate-500 font-medium">Defina los componentes y analice el margen</p>
                </div>
              </div>
              <button 
                onClick={() => setShowModal(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <FiMinus size={24} />
              </button>
            </div>

            <div className="flex flex-col lg:flex-row divide-y lg:divide-y-0 lg:divide-x divide-slate-100 overflow-hidden">
              <div className="p-6 overflow-y-auto max-h-[70vh] custom-scrollbar flex-1">
                <form id="receta-form" onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Producto de Venta</label>
                      <ReactSelect 
                        options={opcionesProductos}
                        value={opcionesProductos.find(o => o.value === form.id_producto)}
                        onChange={val => handleSelectChange(val, "id_producto")}
                        placeholder="Busque un producto..."
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Identificador de Receta</label>
                      <input 
                        type="text"
                        name="nombre"
                        value={form.nombre}
                        onChange={handleChange}
                        placeholder="Ej: Receta Estándar"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm font-medium"
                        required
                      />
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-100">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                        <FiLayers className="text-indigo-500" />
                        Insumos Requeridos
                      </h4>
                      <button 
                        type="button"
                        onClick={handleAddDetalle}
                        className="text-indigo-600 hover:text-indigo-700 font-bold text-xs flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 rounded-lg transition-all"
                      >
                        <FiPlus /> Insumo
                      </button>
                    </div>

                    <div className="space-y-3">
                      {form.detalle.map((d, idx) => {
                        const insumoActual = insumos.find(i => i.id.toString() === d.id_cocina_insumo.toString());
                        const costoLinea = (parseFloat(insumoActual?.price || 0) * parseFloat(d.cantidad || 0));
                        return (
                          <div key={idx} className="flex flex-col sm:flex-row gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100 items-end sm:items-center animate-fade-in relative">
                            <div className="flex-1 w-full flex flex-col gap-1">
                               <label className="text-[10px] font-bold text-slate-400 uppercase">Insumo</label>
                               <ReactSelect 
                                options={opcionesInsumos}
                                value={opcionesInsumos.find(o => o.value === d.id_cocina_insumo)}
                                onChange={val => handleDetalleChange(idx, "id_cocina_insumo", val?.value || "")}
                                placeholder="Seleccione..."
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                               />
                            </div>
                            <div className="w-full sm:w-32 flex flex-col gap-1">
                               <label className="text-[10px] font-bold text-slate-400 uppercase">Cantidad</label>
                               <input 
                                type="number"
                                step="0.0001"
                                value={d.cantidad}
                                onChange={(e) => handleDetalleChange(idx, "cantidad", e.target.value)}
                                placeholder="0.00"
                                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
                                required
                               />
                            </div>
                            <div className="hidden sm:block w-24 text-right pr-2">
                               <p className="text-[10px] font-bold text-slate-400 uppercase">Costo</p>
                               <p className="text-sm font-bold text-slate-600">${costoLinea.toFixed(2)}</p>
                            </div>
                            <button 
                              type="button"
                              onClick={() => handleRemoveDetalle(idx)}
                              className="p-2 text-rose-500 hover:bg-rose-100 rounded-lg transition-colors mb-0.5"
                            >
                              <FiTrash2 size={18} />
                            </button>
                          </div>
                        );
                      })}
                      {form.detalle.length === 0 && (
                        <div className="text-center py-10 border-2 border-dashed border-slate-200 rounded-2xl text-slate-400 italic text-sm">
                           No hay insumos en esta receta.
                        </div>
                      )}
                    </div>
                  </div>
                </form>
              </div>

              {/* Sidebar de Resumen Financiero en Vivo */}
              <div className="w-full lg:w-72 p-6 bg-slate-50 flex flex-col justify-between">
                 <div className="space-y-4">
                    <h4 className="font-bold text-slate-800 text-sm flex items-center gap-2 mb-4">
                       <FiTrendingUp className="text-indigo-600" /> Resumen en Vivo
                    </h4>
                    <div className="space-y-3">
                       <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
                          <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Costo de Insumos</p>
                          <p className="text-2xl font-black text-slate-700">${currentFinancials.totalCosto.toFixed(2)}</p>
                       </div>
                       <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
                          <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Precio de Venta</p>
                          <p className="text-2xl font-black text-slate-700">${currentFinancials.precioVenta.toFixed(2)}</p>
                       </div>
                       <div className={`p-4 rounded-xl shadow-md border ${currentFinancials.ganancia > 0 ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-rose-600 border-rose-500 text-white'}`}>
                          <p className="text-[10px] font-bold uppercase mb-1 opacity-80">Ganancia Estimada</p>
                          <p className="text-2xl font-black">${currentFinancials.ganancia.toFixed(2)}</p>
                          <div className="mt-2 text-xs font-bold bg-white/20 inline-block px-2 py-1 rounded">
                             Margen: {currentFinancials.margen.toFixed(1)}%
                          </div>
                       </div>
                    </div>
                 </div>
                 
                 <div className="mt-8 flex flex-col gap-2">
                    <button 
                      form="receta-form"
                      disabled={submitting}
                      className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 flex items-center justify-center gap-2"
                    >
                      {submitting && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>}
                      {editMode ? "Actualizar Receta" : "Confirmar Receta"}
                    </button>
                    <button 
                      onClick={() => setShowModal(false)}
                      className="w-full py-3 bg-white border border-slate-300 text-slate-600 rounded-xl font-bold hover:bg-slate-50 transition-all text-sm"
                    >
                      Cancelar
                    </button>
                 </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Recetas;
